interface StarSound {
  oscillator: OscillatorNode;
  lfo: OscillatorNode;
  lfoGain: GainNode;
  gain: GainNode;
  modulationGain: GainNode;
  panner: StereoPannerNode;
  x: number;
  z: number;
}

export class AudioManager {
  private context: AudioContext;
  private starCollectBuffer: AudioBuffer | null = null;
  private shortCircuitBuffer: AudioBuffer | null = null;
  private windWhooshBuffer: AudioBuffer | null = null;
  private webTrapBuffer: AudioBuffer | null = null;
  private breadcrumbsPickupBuffer: AudioBuffer | null = null;
  private arrowPickupBuffer: AudioBuffer | null = null;
  private masterGain: GainNode;
  private starSounds: StarSound[] = [];
  private readonly starHumMinFrequency = 50;
  private readonly starHumMaxFrequency = 80;
  private readonly starHumMaxGain = 0.15;
  private readonly starHumRadius = 8;  // Distance at which sound fades to zero
  private readonly lfoMinFrequency = 0.75;
  private readonly lfoMaxFrequency = 1.75;
  private readonly lfoDepth = 0.1;    // ±10% intensity modulation
  private readonly shortCircuitMaxGain = 0.25;
  private readonly shortCircuitRadius = 10;

  constructor() {
    this.context = new AudioContext();
    this.masterGain = this.context.createGain();
    this.masterGain.connect(this.context.destination);

    // Pre-generate sound buffers
    this.generateStarCollectBuffer();
    this.generateShortCircuitBuffer();
    this.generateWindWhooshBuffer();
    this.generateWebTrapBuffer();
    this.generateBreadcrumbsPickupBuffer();
    this.generateArrowPickupBuffer();
  }

  private generateStarCollectBuffer(): void {
    const sampleRate = this.context.sampleRate;
    const duration = 0.1;  // 100ms
    const numSamples = Math.floor(sampleRate * duration);

    this.starCollectBuffer = this.context.createBuffer(1, numSamples, sampleRate);
    const channelData = this.starCollectBuffer.getChannelData(0);

    const startFreq = 500;
    const endFreq = 450;
    const startGain = 0.3;

    let phase = 0;
    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      const progress = t / duration;

      // Linear frequency sweep from 500Hz to 450Hz
      const freq = startFreq + (endFreq - startFreq) * progress;

      // Linear gain fade from 0.3 to 0
      const gain = startGain * (1 - progress);

      // Generate sine wave sample
      channelData[i] = Math.sin(phase) * gain;

      // Advance phase based on current frequency
      phase += (2 * Math.PI * freq) / sampleRate;
    }
  }

  private generateShortCircuitBuffer(): void {
    const sampleRate = this.context.sampleRate;
    const duration = 0.18;  // 180ms — longer for more menace
    const numSamples = Math.floor(sampleRate * duration);

    this.shortCircuitBuffer = this.context.createBuffer(1, numSamples, sampleRate);
    const channelData = this.shortCircuitBuffer.getChannelData(0);

    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      const progress = t / duration;

      // Envelope: sharp attack, slow decay with a second bump
      const env1 = Math.exp(-progress * 6);
      const env2 = Math.exp(-Math.pow((progress - 0.3) * 8, 2)) * 0.5; // secondary arc
      const envelope = env1 + env2;

      // White noise with hard clipping (crackle)
      const noise = Math.random() * 2 - 1;
      const clippedNoise = Math.sign(noise) * Math.min(1, Math.abs(noise * 4)) * 0.5;

      // 60Hz heavily distorted square-ish buzz (fundamental)
      const saw60 = ((t * 60) % 1) * 2 - 1;
      const buzz60 = Math.tanh(saw60 * 6);

      // 120Hz harmonic — adds thickness
      const saw120 = ((t * 120) % 1) * 2 - 1;
      const buzz120 = Math.tanh(saw120 * 5);

      // 180Hz harmonic — adds edge
      const saw180 = ((t * 180) % 1) * 2 - 1;
      const buzz180 = Math.tanh(saw180 * 4);

      // Rapid stutter gate (~30Hz) for angry arcing texture
      const gate = (Math.sin(2 * Math.PI * 30 * t) > -0.2) ? 1.0 : 0.15;

      // High-frequency sizzle
      const sizzle = noise * Math.sin(2 * Math.PI * 3000 * t) * 0.3;

      // Mix: buzz-heavy blend
      const buzzMix = buzz60 * 0.4 + buzz120 * 0.25 + buzz180 * 0.15;
      const sample = (buzzMix + clippedNoise * 0.3 + sizzle * 0.1) * envelope * gate;
      channelData[i] = sample * 0.9;
    }
  }

  private generateWindWhooshBuffer(): void {
    const sampleRate = this.context.sampleRate;
    const duration = 0.6;
    const numSamples = Math.floor(sampleRate * duration);

    this.windWhooshBuffer = this.context.createBuffer(1, numSamples, sampleRate);
    const channelData = this.windWhooshBuffer.getChannelData(0);

    // Simple IIR bandpass state
    let bp0 = 0, bp1 = 0, lp0 = 0, lp1 = 0;

    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      const progress = t / duration;

      // Envelope: fast attack, sustained body, gentle fade
      const attack = 1 - Math.exp(-progress * 20);
      const decay = Math.exp(-Math.max(0, progress - 0.15) * 4);
      const envelope = attack * decay;

      // Center frequency sweeps up from ~200Hz to ~1500Hz
      const centerFreq = 200 + 1300 * progress * progress;
      // Bandwidth widens as it sweeps up
      const bandwidth = centerFreq * 0.8;

      // Two-pole bandpass via state-variable filter
      const w0 = 2 * Math.PI * centerFreq / sampleRate;
      const q = centerFreq / bandwidth;

      // White noise source
      const noise = Math.random() * 2 - 1;

      // Apply bandpass via state-variable filter
      const hp = noise - lp0 - bp0 * (1 / q);
      bp0 += w0 * hp;
      lp0 += w0 * bp0;

      // Second pass for steeper rolloff
      const hp1 = bp0 - lp1 - bp1 * (1 / q);
      bp1 += w0 * hp1;
      lp1 += w0 * bp1;

      // Add a low rumble layer (filtered noise with slow oscillation)
      const rumble = noise * Math.sin(2 * Math.PI * 80 * t) * 0.3;

      const sample = (bp1 * 0.7 + rumble * 0.3) * envelope;
      channelData[i] = sample * 0.5;
    }
  }

  private generateWebTrapBuffer(): void {
    const sampleRate = this.context.sampleRate;
    const duration = 0.5;
    const numSamples = Math.floor(sampleRate * duration);

    this.webTrapBuffer = this.context.createBuffer(1, numSamples, sampleRate);
    const channelData = this.webTrapBuffer.getChannelData(0);

    let phase = 0;
    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      const progress = t / duration;

      // Exponential sweep from 800Hz down to 200Hz
      const freq = 800 * Math.pow(200 / 800, progress);

      // Envelope: quick attack, gradual decay
      const envelope = Math.exp(-progress * 3) * (1 - Math.exp(-progress * 40));

      // Sine + harmonic for richness
      const sample = (Math.sin(phase) * 0.7 + Math.sin(phase * 2) * 0.3) * envelope * 0.4;
      channelData[i] = sample;

      phase += (2 * Math.PI * freq) / sampleRate;
    }
  }

  private generateBreadcrumbsPickupBuffer(): void {
    const sampleRate = this.context.sampleRate;
    const duration = 0.3;
    const numSamples = Math.floor(sampleRate * duration);

    this.breadcrumbsPickupBuffer = this.context.createBuffer(1, numSamples, sampleRate);
    const channelData = this.breadcrumbsPickupBuffer.getChannelData(0);

    let phase = 0;
    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      const progress = t / duration;

      // Ascending tone 400Hz to 800Hz
      const freq = 400 + 400 * progress;
      const envelope = (1 - progress) * (1 - Math.exp(-progress * 30));

      channelData[i] = Math.sin(phase) * envelope * 0.3;
      phase += (2 * Math.PI * freq) / sampleRate;
    }
  }

  private generateArrowPickupBuffer(): void {
    const sampleRate = this.context.sampleRate;
    const duration = 0.25;
    const numSamples = Math.floor(sampleRate * duration);

    this.arrowPickupBuffer = this.context.createBuffer(1, numSamples, sampleRate);
    const channelData = this.arrowPickupBuffer.getChannelData(0);

    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      const progress = t / duration;

      // Two staggered pings
      const ping1Env = Math.exp(-progress * 12) * (1 - Math.exp(-progress * 60));
      const ping1 = Math.sin(2 * Math.PI * 600 * t) * ping1Env;

      const ping2Start = 0.06; // Second ping starts 60ms later
      const ping2T = Math.max(0, t - ping2Start);
      const ping2Progress = ping2T / (duration - ping2Start);
      const ping2Env = ping2T > 0 ? Math.exp(-ping2Progress * 10) * (1 - Math.exp(-ping2Progress * 60)) : 0;
      const ping2 = Math.sin(2 * Math.PI * 900 * t) * ping2Env;

      channelData[i] = (ping1 * 0.5 + ping2 * 0.5) * 0.3;
    }
  }

  playWebTrapSound(): void {
    if (this.context.state !== 'running' || !this.webTrapBuffer) return;
    const source = this.context.createBufferSource();
    source.buffer = this.webTrapBuffer;
    source.connect(this.masterGain);
    source.start();
  }

  playBreadcrumbsPickup(): void {
    if (this.context.state !== 'running' || !this.breadcrumbsPickupBuffer) return;
    const source = this.context.createBufferSource();
    source.buffer = this.breadcrumbsPickupBuffer;
    source.connect(this.masterGain);
    source.start();
  }

  playArrowPickup(): void {
    if (this.context.state !== 'running' || !this.arrowPickupBuffer) return;
    const source = this.context.createBufferSource();
    source.buffer = this.arrowPickupBuffer;
    source.connect(this.masterGain);
    source.start();
  }

  playWindWhoosh(): void {
    if (this.context.state !== 'running' || !this.windWhooshBuffer) return;

    const source = this.context.createBufferSource();
    source.buffer = this.windWhooshBuffer;
    source.connect(this.masterGain);
    source.start();
  }

  playShortCircuitSound(lightX: number, lightZ: number, playerX: number, playerZ: number, playerYaw: number): void {
    if (this.context.state !== 'running' || !this.shortCircuitBuffer) return;

    const dx = lightX - playerX;
    const dz = lightZ - playerZ;
    const distance = Math.sqrt(dx * dx + dz * dz);

    // Skip if beyond audible radius
    if (distance >= this.shortCircuitRadius) return;

    // Volume: linear falloff with distance
    const volume = this.shortCircuitMaxGain * (1 - distance / this.shortCircuitRadius);

    // Stereo pan based on angle relative to player heading
    const bearingToLight = Math.atan2(-dx, -dz);
    const relativeAngle = bearingToLight - playerYaw;
    const pan = -Math.sin(relativeAngle);

    // One-shot chain: source → gain → panner → master
    const source = this.context.createBufferSource();
    source.buffer = this.shortCircuitBuffer;

    const gainNode = this.context.createGain();
    gainNode.gain.value = volume;

    const pannerNode = this.context.createStereoPanner();
    pannerNode.pan.value = Math.max(-1, Math.min(1, pan));

    source.connect(gainNode);
    gainNode.connect(pannerNode);
    pannerNode.connect(this.masterGain);

    source.start();
  }

  // Call this on first user interaction (e.g., pointer lock click)
  // to ensure audio context is ready before sounds need to play
  resume(): void {
    if (this.context.state === 'suspended') {
      this.context.resume();
    }
  }

  playStarCollect(): void {
    if (this.context.state !== 'running' || !this.starCollectBuffer) return;

    // Create a buffer source node to play the pre-generated sound
    const source = this.context.createBufferSource();
    source.buffer = this.starCollectBuffer;
    source.connect(this.masterGain);
    source.start();
  }

  // Set up ambient hum sounds for all stars
  setupStarSounds(stars: Array<{ position: { x: number; z: number } }>): void {
    // Clean up any existing star sounds
    this.stopAllStarSounds();

    const starCount = stars.length;

    for (let i = 0; i < starCount; i++) {
      const star = stars[i];

      // Calculate unique frequency for this star (spread from 50-80 Hz)
      const freqRange = this.starHumMaxFrequency - this.starHumMinFrequency;
      const frequency = this.starHumMinFrequency + (freqRange * i) / Math.max(1, starCount - 1);

      // Calculate unique LFO frequency for this star (spread from 0.75-1.75 Hz)
      const lfoRange = this.lfoMaxFrequency - this.lfoMinFrequency;
      const lfoFrequency = this.lfoMinFrequency + (lfoRange * i) / Math.max(1, starCount - 1);

      // Main oscillator
      const oscillator = this.context.createOscillator();
      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;

      // Distance-based gain
      const gain = this.context.createGain();
      gain.gain.value = 0;  // Start silent, will be updated based on distance

      // Modulation gain (LFO target) - base value of 1.0
      const modulationGain = this.context.createGain();
      modulationGain.gain.value = 1.0;

      // LFO oscillator
      const lfo = this.context.createOscillator();
      lfo.type = 'sine';
      lfo.frequency.value = lfoFrequency;

      // LFO gain (scales LFO output to ±10%)
      const lfoGain = this.context.createGain();
      lfoGain.gain.value = this.lfoDepth;

      // Stereo panner
      const panner = this.context.createStereoPanner();
      panner.pan.value = 0;

      // Connect audio chain: oscillator → gain → modulationGain → panner → master
      oscillator.connect(gain);
      gain.connect(modulationGain);
      modulationGain.connect(panner);
      panner.connect(this.masterGain);

      // Connect LFO chain: lfo → lfoGain → modulationGain.gain
      lfo.connect(lfoGain);
      lfoGain.connect(modulationGain.gain);

      // Start oscillators
      oscillator.start();
      lfo.start();

      this.starSounds.push({
        oscillator,
        lfo,
        lfoGain,
        gain,
        modulationGain,
        panner,
        x: star.position.x,
        z: star.position.z
      });
    }
  }

  // Update star sound volumes and panning based on player position and heading
  updateStarSounds(playerX: number, playerZ: number, playerYaw: number): void {
    if (this.context.state !== 'running') return;

    const currentTime = this.context.currentTime;
    const smoothingTime = 0.05;  // 50ms smoothing to avoid crackling

    for (const starSound of this.starSounds) {
      const dx = starSound.x - playerX;
      const dz = starSound.z - playerZ;
      const distance = Math.sqrt(dx * dx + dz * dz);

      // Calculate volume based on distance (inverse linear falloff)
      let volume = 0;
      if (distance < this.starHumRadius) {
        volume = this.starHumMaxGain * (1 - distance / this.starHumRadius);
      }
      // Use setTargetAtTime for smooth transitions (avoids crackling)
      starSound.gain.gain.setTargetAtTime(volume, currentTime, smoothingTime);

      // Calculate panning based on angle relative to player heading
      // Bearing from player to star (matching yaw convention: 0 = -Z, increasing = counter-clockwise)
      const bearingToStar = Math.atan2(-dx, -dz);
      // Relative angle (positive = star is to the left of player's heading)
      const relativeAngle = bearingToStar - playerYaw;
      // Use sine to get pan value (-1 left, +1 right)
      const pan = -Math.sin(relativeAngle);
      // Use setTargetAtTime for smooth transitions (avoids crackling)
      starSound.panner.pan.setTargetAtTime(pan, currentTime, smoothingTime);
    }
  }

  // Stop the sound for a specific star (by position)
  stopStarSound(x: number, z: number): void {
    const index = this.starSounds.findIndex(s => s.x === x && s.z === z);
    if (index >= 0) {
      const starSound = this.starSounds[index];
      starSound.oscillator.stop();
      starSound.lfo.stop();
      starSound.oscillator.disconnect();
      starSound.lfo.disconnect();
      starSound.lfoGain.disconnect();
      starSound.gain.disconnect();
      starSound.modulationGain.disconnect();
      starSound.panner.disconnect();
      this.starSounds.splice(index, 1);
    }
  }

  // Stop all star sounds (used when generating new maze)
  stopAllStarSounds(): void {
    for (const starSound of this.starSounds) {
      starSound.oscillator.stop();
      starSound.lfo.stop();
      starSound.oscillator.disconnect();
      starSound.lfo.disconnect();
      starSound.lfoGain.disconnect();
      starSound.gain.disconnect();
      starSound.modulationGain.disconnect();
      starSound.panner.disconnect();
    }
    this.starSounds = [];
  }
}
